/**
 * La barra di navigazione laterale.
 *
 * Chiusa mostra solo le icone; al passaggio del mouse si allarga coi titoli.
 * Due regole che vengono da vincoli veri, non da gusto:
 *
 * 1. **Allargandosi COPRE, non spinge.** Il tabellone di M3 ha 11 colonne e il
 *    dock di Aida ne occupa 420: l'11-08 abbiamo tolto il `max-width` alla tab
 *    «Carichi previsti» perché non ci stavano. Se la barra spingesse il
 *    contenuto, le colonne si ricomporrebbero ogni volta che il mouse sfiora il
 *    bordo sinistro, mentre Marina sta leggendo una riga. Quindi la barra è in
 *    `position: fixed` e il contenuto ha un margine pari alla sola larghezza
 *    CHIUSA: allargandosi va sopra, e sotto non si muove niente.
 *
 * 2. **Lo spillo è l'unica cosa che muove il layout.** Sbirciare col mouse e
 *    tenere aperto sono due intenzioni diverse: la seconda è una scelta
 *    esplicita, e si ricorda per utente (`localStorage`).
 *
 * Regola di arbitrato per i casi dubbi: **la barra serve a spostarsi TRA i
 * moduli, le tab a muoversi DENTRO un modulo.** I `children` di un modulo sono
 * scorciatoie che entrano diritte in una sezione, non le sue tab.
 */

import { Bot, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useAdminAgenti } from "@/hooks/useAdminAgenti";
import { useAdminNotifiche } from "@/hooks/useAdminShell";
import { useAgenti } from "@/hooks/useAgenti";
import { useMe } from "@/hooks/useMe";
import { useMyPermessi } from "@/hooks/usePermessi";
import { ADMIN_NAV } from "@/lib/admin-nav";
import {
  getVisibleZone,
  isMaitimeStaff,
  NAV_ADMIN,
  NAV_AGENTI,
  type NavItem,
  SEZIONI_AGENTE,
} from "@/lib/nav-config";
import { cn } from "@/lib/utils";

/** Larghezze della barra. La chiusa è anche il margine del contenuto: se la
 *  cambi, cambiala in `RootLayout` (che la legge da qui). */
export const RAIL_CHIUSA = 56;
export const RAIL_APERTA = 232;

const CHIAVE_SPILLO = "misterpet.rail.spillo";

function leggiSpillo(): boolean {
  try {
    return localStorage.getItem(CHIAVE_SPILLO) === "1";
  } catch {
    // Safari in navigazione privata: la barra funziona lo stesso, si dimentica.
    return false;
  }
}

/**
 * I colori della barra, raccolti in un posto solo.
 *
 * Enrico vuole provare il **verde pieno** visto nel mockup del calendario
 * (12-08): «poi vedremo se lasciarlo o ripristinarlo». Tenerli qui è ciò che
 * rende quel «poi» una riga da cambiare invece di un giro di ritocchi su venti
 * classi sparse — `const TEMA = TEMA_CHIARO` e la barra torna com'era.
 */
export const TEMA_VERDE = {
  barra: "border-brand-800 bg-brand-700",
  logo: "bg-white/15 text-white",
  titolo: "text-white/45",
  voce: "text-white/70 hover:bg-white/10 hover:text-white",
  voceAttiva: "bg-white/15 font-semibold text-white",
  indicatore: "bg-white",
  figlio: "text-white/65 hover:bg-white/10 hover:text-white",
  figlioAttivo: "font-semibold text-white",
  separatore: "border-white/10",
  anello: "focus-visible:ring-white/70",
  anelloPuntino: "ring-brand-700",
  ombra: "shadow-[8px_0_28px_-12px_rgb(15,23,42,0.45)]",
} as const;

export const TEMA_CHIARO = {
  barra: "border-border bg-background",
  logo: "bg-brand-700 text-white",
  titolo: "text-muted-foreground",
  voce: "text-muted-foreground hover:bg-muted hover:text-foreground",
  voceAttiva: "bg-brand-50 font-semibold text-brand-700",
  indicatore: "bg-brand-700",
  figlio: "text-muted-foreground hover:bg-muted hover:text-foreground",
  figlioAttivo: "font-semibold text-brand-700",
  separatore: "border-border",
  anello: "focus-visible:ring-ring",
  anelloPuntino: "ring-background",
  ombra: "shadow-[8px_0_28px_-12px_rgb(15,23,42,0.28)]",
} as const;

/** Il tema in uso: cambiare QUESTA riga per tornare alla barra chiara. */
const TEMA = TEMA_VERDE;

export function SideRail() {
  const { data: me } = useMe();
  const { data: perms } = useMyPermessi();
  const { pathname } = useLocation();

  const [spillo, setSpillo] = useState(leggiSpillo);
  const [hover, setHover] = useState(false);
  const aperta = spillo || hover;

  useEffect(() => {
    try {
      localStorage.setItem(CHIAVE_SPILLO, spillo ? "1" : "0");
    } catch {
      /* niente da fare: resta la scelta di questa sessione */
    }
  }, [spillo]);

  const zone = getVisibleZone(me ?? null, perms);
  const staff = isMaitimeStaff(me ?? null);

  return (
    <nav
      aria-label="Sezioni"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ width: aperta ? RAIL_APERTA : RAIL_CHIUSA }}
      className={cn(
        // z-50: allargandosi passa sopra l'header (z-40), che a sua volta sta
        // sopra il dock di Aida (z-30) perché il pannello notifiche non ci
        // finisca sotto.
        "fixed bottom-0 left-0 top-0 z-50 flex flex-col overflow-hidden border-r",
        TEMA.barra,
        "transition-[width] duration-150 ease-out motion-reduce:transition-none",
        // L'ombra c'è solo quando la barra sta COPRENDO qualcosa: se è spillata
        // occupa spazio suo e un'ombra la farebbe sembrare staccata.
        hover && !spillo && TEMA.ombra,
      )}
    >
      {/* Testa: logo e spillo. Il logo torna a fare solo il logo. */}
      <div className="flex h-14 flex-none items-center gap-2 px-3">
        <NavLink
          to="/"
          className={cn(
            "grid size-8 flex-none place-items-center rounded-lg font-serif text-sm font-bold leading-none",
            TEMA.logo,
          )}
          aria-label="Mister Pet"
        >
          M
        </NavLink>
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-sm font-semibold tracking-tight transition-opacity duration-100",
            aperta ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          Mister Pet
        </span>
        <button
          type="button"
          onClick={() => setSpillo((v) => !v)}
          aria-pressed={spillo}
          title={spillo ? "Lascia che la barra si chiuda" : "Tieni la barra aperta"}
          className={cn(
            "grid size-7 flex-none place-items-center rounded-md transition-opacity duration-100",
            TEMA.voce,
            "focus-visible:outline-none focus-visible:ring-2",
            TEMA.anello,
            aperta ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          {spillo ? (
            <PanelLeftClose className="size-4" />
          ) : (
            <PanelLeftOpen className="size-4" />
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden pb-3">
        {zone.map((z) => (
          <div key={z.titolo ?? "-"} className={cn("border-t py-1.5 first:border-t-0", TEMA.separatore)}>
            <Titolo testo={z.titolo} aperta={aperta} />
            {z.voci.map((v) => (
              <Voce key={v.key} item={v} aperta={aperta} pathname={pathname} />
            ))}
          </div>
        ))}

        {/* Agenti: zona dinamica, i figli vengono dal roster e non dalla
            configurazione — un agente futuro compare da sé. */}
        <div className={cn("border-t py-1.5", TEMA.separatore)}>
          <VoceAgenti aperta={aperta} pathname={pathname} staff={staff} />
        </div>

        {staff && (
          <div className={cn("mt-1.5 border-t py-1.5", TEMA.separatore)}>
            <VoceAdmin aperta={aperta} pathname={pathname} />
          </div>
        )}
      </div>
    </nav>
  );
}

function Titolo({ testo, aperta }: { testo?: string; aperta: boolean }) {
  if (!testo) return null;
  return (
    <div
      className={cn(
        "flex h-5 items-center whitespace-nowrap px-4 text-[10px] font-bold uppercase tracking-[0.1em]",
        TEMA.titolo,
        "transition-opacity duration-100",
        aperta ? "opacity-100" : "opacity-0",
      )}
    >
      {testo}
    </div>
  );
}

/** Una voce è attiva anche quando sei su una delle sue sezioni figlie. */
function eAttiva(item: NavItem, pathname: string): boolean {
  const paths = [item.path, ...(item.children ?? []).map((c) => c.path)].filter(
    Boolean,
  ) as string[];
  return paths.some((p) => (p === "/" ? pathname === "/" : pathname.startsWith(p)));
}

function Voce({
  item,
  aperta,
  pathname,
}: {
  item: NavItem;
  aperta: boolean;
  pathname: string;
}) {
  const attiva = eAttiva(item, pathname);
  const Icona = item.icon;
  return (
    <>
      <NavLink
        to={item.path ?? "/"}
        end={item.path === "/"}
        className={cn(
          "relative flex h-9 items-center gap-3 whitespace-nowrap px-4 text-[13px] font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
          TEMA.anello,
          attiva ? TEMA.voceAttiva : TEMA.voce,
        )}
        // A barra chiusa il titolo non si vede: il tooltip nativo è l'unica
        // etichetta disponibile per chi non conosce ancora le icone.
        title={aperta ? undefined : item.label}
      >
        {attiva && (
          <span className={cn("absolute inset-y-1.5 left-0 w-[3px] rounded-r", TEMA.indicatore)} />
        )}
        {Icona && <Icona className="size-[17px] flex-none" />}
        <span
          className={cn(
            "min-w-0 flex-1 truncate transition-opacity duration-100",
            aperta ? "opacity-100" : "opacity-0",
          )}
        >
          {item.label}
        </span>
      </NavLink>

      {/* Le sezioni del modulo compaiono solo a barra aperta e solo se ci sei
          dentro: chiusa non c'è spazio, e altrove sarebbero rumore. */}
      {aperta && attiva && item.children && (
        <div className="pb-1.5">
          {item.children.map((c) => (
            <Figlio key={c.key} item={c} pathname={pathname} />
          ))}
        </div>
      )}
    </>
  );
}

function Figlio({ item, pathname }: { item: NavItem; pathname: string }) {
  // Confronto esatto: «Carichi previsti» e «Partenze» sono due rotte diverse, e
  // con `startsWith` la prima (che è la radice del modulo) resterebbe sempre accesa.
  const attivo = pathname === item.path;
  return (
    <NavLink
      to={item.path ?? "/"}
      className={cn(
        "flex h-7 items-center gap-2 whitespace-nowrap pl-[43px] pr-4 text-[12.5px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
          TEMA.anello,
        attivo ? TEMA.figlioAttivo : TEMA.figlio,
      )}
    >
      <span
        className={cn("size-1 flex-none rounded-full bg-current", !attivo && "opacity-45")}
      />
      <span className="min-w-0 truncate">{item.label}</span>
    </NavLink>
  );
}

/**
 * «Agenti»: voce di primo livello col cervello, e sotto i nomi degli agenti.
 *
 * Era una zona «Aida» con dentro Memoria; Enrico l'ha rovesciata (12-08) perché
 * gli agenti saranno più d'uno e l'elenco deve reggerli. È l'unica voce
 * **dinamica** della barra: i figli arrivano dal roster.
 *
 * Due scelte prese e dichiarate, in mancanza di indicazione:
 *  - gli agenti **«in arrivo» si vedono, spenti**, con l'etichetta — far vedere
 *    cosa sta arrivando è parte del racconto del progetto, e il portale lo fa
 *    già così per le sezioni non pronte;
 *  - **il nome dell'agente porta dove ha davvero qualcosa**: alla sua prima
 *    sezione utente (Aida → Memoria); per lo staff, se sezioni non ne ha, alla
 *    sua vigilanza in Admin. Un agente che non porta da nessuna parte resta
 *    un'intestazione spenta, invece di essere una voce che non fa niente.
 */
function VoceAgenti({
  aperta,
  pathname,
  staff,
}: {
  aperta: boolean;
  pathname: string;
  staff: boolean;
}) {
  const { data } = useAgenti();
  const agenti = data?.agenti ?? [];
  const Icona = NAV_AGENTI.icon;

  // Le sezioni utente di ciascun agente, più la porta per lo staff.
  const voci = agenti.map((a) => {
    const sezioni = SEZIONI_AGENTE[a.slug] ?? [];
    const destinazione =
      sezioni[0]?.path ?? (staff && a.stato !== "in_arrivo" ? `/admin/agenti/${a.slug}` : null);
    return { ...a, sezioni, destinazione };
  });

  // Chi non ha destinazione né è «in arrivo» non ha niente da dire in una barra:
  // per un cliente sarebbe un nome che non fa niente.
  const visibili = voci.filter((v) => v.destinazione || (staff && v.stato === "in_arrivo"));
  if (visibili.length === 0) return null;

  const dentro = visibili.some(
    (v) =>
      v.sezioni.some((s) => s.path && pathname.startsWith(s.path)) ||
      pathname.startsWith(`/admin/agenti/${v.slug}`),
  );

  return (
    <>
      <div
        className={cn(
          "relative flex h-9 items-center gap-3 whitespace-nowrap px-4 text-[13px] font-medium",
          dentro ? TEMA.figlioAttivo : TEMA.voce,
        )}
        title={aperta ? undefined : NAV_AGENTI.label}
      >
        {dentro && (
          <span className={cn("absolute inset-y-1.5 left-0 w-[3px] rounded-r", TEMA.indicatore)} />
        )}
        <Icona className="size-[17px] flex-none" />
        <span
          className={cn(
            "min-w-0 flex-1 truncate transition-opacity duration-100",
            aperta ? "opacity-100" : "opacity-0",
          )}
        >
          {NAV_AGENTI.label}
        </span>
      </div>

      {aperta && (
        <div className="pb-1">
          {visibili.map((a) =>
            a.destinazione ? (
              <NavLink
                key={a.slug}
                to={a.destinazione}
                className={({ isActive }) =>
                  cn(
                    "flex h-7 items-center gap-2 whitespace-nowrap pl-[43px] pr-4 text-[12.5px] transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
          TEMA.anello,
                    isActive ? TEMA.figlioAttivo : TEMA.figlio,
                  )
                }
              >
                <Bot className="size-3.5 flex-none opacity-70" />
                <span className="min-w-0 flex-1 truncate">{a.nome}</span>
                <span className="size-1.5 flex-none rounded-full bg-emerald-500" aria-hidden />
              </NavLink>
            ) : (
              <div
                key={a.slug}
                className={cn("flex h-7 items-center gap-2 whitespace-nowrap pl-[43px] pr-4 text-[12.5px]", TEMA.figlio, "opacity-60")}
                title="Agente in arrivo"
              >
                <Bot className="size-3.5 flex-none opacity-50" />
                <span className="min-w-0 flex-1 truncate">{a.nome}</span>
                <span className="flex-none text-[9.5px] font-semibold uppercase tracking-wide">
                  in arrivo
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
}

/**
 * Admin: una voce sola che si apre sulle **16 pagine** del backoffice,
 * raggruppate sotto le loro 7 famiglie.
 *
 * Le famiglie sono **intestazioni, non destinazioni**: ogni pagina resta a un
 * clic come nella vecchia sidebar. Assorbirla qui evita due colonne di
 * navigazione affiancate quando entri in `/admin`.
 */
function VoceAdmin({ aperta, pathname }: { aperta: boolean; pathname: string }) {
  const dentro = pathname.startsWith("/admin");
  const agenti = useAdminAgenti();
  const notifiche = useAdminNotifiche();

  const tipiAttivi = new Set((notifiche.data?.notifiche ?? []).map((n) => n.tipo));
  const agentiAttivi = (agenti.data?.agenti ?? []).filter((a) => a.stato === "attivo");
  const Icona = NAV_ADMIN.icon!;

  return (
    <>
      <NavLink
        to="/admin"
        end
        className={cn(
          "relative flex h-9 items-center gap-3 whitespace-nowrap px-4 text-[13px] font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
          TEMA.anello,
          dentro ? TEMA.voceAttiva : TEMA.voce,
        )}
        title={aperta ? undefined : NAV_ADMIN.label}
      >
        {dentro && (
          <span className={cn("absolute inset-y-1.5 left-0 w-[3px] rounded-r", TEMA.indicatore)} />
        )}
        <Icona className="size-[17px] flex-none" />
        <span
          className={cn(
            "min-w-0 flex-1 truncate transition-opacity duration-100",
            aperta ? "opacity-100" : "opacity-0",
          )}
        >
          {NAV_ADMIN.label}
        </span>
        {/* Chiusa, le notifiche del backoffice restano visibili come puntino:
            il segnale sopravvive, perde solo il dettaglio di quale famiglia. */}
        {!aperta && tipiAttivi.size > 0 && (
          <span className={cn("absolute left-[26px] top-2 size-[7px] rounded-full bg-red-500 ring-2", TEMA.anelloPuntino)} />
        )}
      </NavLink>

      {aperta && dentro && (
        <div className="pb-1.5">
          {ADMIN_NAV.map((gruppo, i) => (
            <div key={gruppo.label ?? i}>
              {gruppo.label && (
                <div className={cn("whitespace-nowrap px-4 pb-0.5 pl-[43px] pt-2 text-[9.5px] font-bold uppercase tracking-[0.09em]", TEMA.titolo)}>
                  {gruppo.label}
                </div>
              )}
              {gruppo.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/admin"}
                  className={({ isActive }) =>
                    cn(
                      "flex h-7 items-center gap-2 whitespace-nowrap pl-[43px] pr-4 text-[12.5px] transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
          TEMA.anello,
                      isActive ? TEMA.figlioAttivo : TEMA.figlio,
                    )
                  }
                >
                  <it.icon className="size-3.5 flex-none opacity-70" />
                  <span className="min-w-0 flex-1 truncate">{it.label}</span>
                  {it.badgeTipo && tipiAttivi.has(it.badgeTipo) && (
                    <span className="size-2 flex-none rounded-full bg-red-500" aria-hidden />
                  )}
                </NavLink>
              ))}
              {/* Gli agenti attivi restano sotto-link derivati dal roster, come
                  nella sidebar che questa voce sostituisce. */}
              {gruppo.label === "Agenti AI" &&
                agentiAttivi.map((a) => (
                  <NavLink
                    key={a.slug}
                    to={`/admin/agenti/${a.slug}`}
                    className={({ isActive }) =>
                      cn(
                        "flex h-7 items-center gap-2 whitespace-nowrap pl-[59px] pr-4 text-[12px] transition-colors",
                        isActive ? TEMA.figlioAttivo : TEMA.figlio,
                      )
                    }
                  >
                    <Bot className="size-3 flex-none" />
                    <span className="min-w-0 flex-1 truncate">{a.nome}</span>
                    <span className="size-1.5 flex-none rounded-full bg-emerald-500" aria-hidden />
                  </NavLink>
                ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
